import { useEffect, useRef, useState } from 'react';

// Fun labels ported verbatim from v1's ThinkingCap. Warm orange vibe.
const THINKING_LABELS: { emoji: string; text: string }[] = [
  { emoji: '🧢', text: 'Thinking cap on' },
  { emoji: '🔮', text: 'Reading the tea leaves' },
  { emoji: '🧪', text: 'Cooking something up' },
  { emoji: '🎯', text: 'Locking in' },
  { emoji: '🧠', text: 'Big brain time' },
  { emoji: '🛠️', text: 'Under the hood' },
  { emoji: '🎰', text: 'Pulling levers' },
  { emoji: '🏄', text: 'Surfing the data' },
  { emoji: '🔬', text: 'Zooming in' },
  { emoji: '🎸', text: 'Riffing on it' },
  { emoji: '🚀', text: 'Firing thrusters' },
  { emoji: '🕵️', text: 'On the case' },
  { emoji: '⚡', text: 'Sparks flying' },
  { emoji: '🧩', text: 'Piecing it together' },
  { emoji: '🎧', text: 'In the zone' },
  { emoji: '🌀', text: 'Spiraling in' },
  { emoji: '🏗️', text: 'Building it out' },
  { emoji: '🎬', text: 'Lights, camera, action' },
  { emoji: '🧲', text: 'Pulling it together' },
  { emoji: '🦾', text: 'Flexing on this' },
  { emoji: '🪄', text: 'Working some magic' },
  { emoji: '📡', text: 'Tuning in' },
  { emoji: '🎪', text: 'Running the show' },
  { emoji: '🔥', text: 'Heating up' },
  { emoji: '🧭', text: 'Finding the way' },
  { emoji: '🎲', text: 'Rolling with it' },
  { emoji: '🌊', text: 'Riding the wave' },
  { emoji: '💡', text: 'Lightbulb moment' },
  { emoji: '🛸', text: 'Beaming down answers' },
  { emoji: '🎤', text: 'Dropping knowledge' },
];

function pickLabel(excludeIdx?: number): { idx: number; label: (typeof THINKING_LABELS)[number] } {
  let idx = Math.floor(Math.random() * THINKING_LABELS.length);
  // Avoid repeating the same label back-to-back.
  if (excludeIdx !== undefined && THINKING_LABELS.length > 1) {
    while (idx === excludeIdx) idx = Math.floor(Math.random() * THINKING_LABELS.length);
  }
  return { idx, label: THINKING_LABELS[idx]! };
}

interface Props {
  /** True while the stream is live. When false, renders nothing. */
  active: boolean;
  /** Most recent tool-call name from the stream (shown after the label as a ticker). */
  currentTool?: string | undefined;
  /** Minimum time between rotations, in ms. Default: 7s. */
  minRotateMs?: number;
  /** Maximum time between rotations, in ms. Default: 14s. */
  maxRotateMs?: number;
}

/**
 * Replaces v1's "Agent is typing…" with a rotating set of playful status
 * phrases + a ticker for the currently-running tool call. Each rotation
 * waits a random duration in [minRotateMs, maxRotateMs] before picking a
 * new label, so the cadence feels organic rather than metronomic. Also
 * rotates immediately when a new tool call arrives.
 */
export function ThinkingCap({ active, currentTool, minRotateMs = 7000, maxRotateMs = 14_000 }: Props) {
  const [state, setState] = useState(() => pickLabel());
  const prevToolRef = useRef<string | undefined>(currentTool);
  const [tickerKey, setTickerKey] = useState(0);

  // Chain random timeouts so the cadence is non-metronomic.
  useEffect(() => {
    if (!active) return;
    const lo = Math.max(1000, Math.min(minRotateMs, maxRotateMs));
    const hi = Math.max(lo, maxRotateMs);
    let timeoutId: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const delay = lo + Math.random() * (hi - lo);
      timeoutId = setTimeout(() => {
        setState((prev) => pickLabel(prev.idx));
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timeoutId);
  }, [active, minRotateMs, maxRotateMs]);

  // Also rotate when a new tool call arrives (visual cue of progress).
  useEffect(() => {
    if (currentTool !== prevToolRef.current) {
      if (prevToolRef.current !== undefined) {
        setState((prev) => pickLabel(prev.idx));
      }
      setTickerKey((k) => k + 1);
      prevToolRef.current = currentTool;
    }
  }, [currentTool]);

  if (!active) return null;

  const tickerText = currentTool ? shortenToolName(currentTool) : 'working';

  return (
    <div
      data-testid="thinking-cap"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: 'var(--accent-softer)',
        border: '1px solid var(--accent-soft)',
        borderRadius: 6,
        fontSize: 11,
        color: 'var(--accent)',
        animation: 'sd-fade-in 200ms ease-out',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--accent)',
          flexShrink: 0,
          animation: 'sd-pulse 1.2s ease-in-out infinite',
        }}
      />
      <span style={{ flexShrink: 0, fontWeight: 500 }}>
        {state.label.emoji} {state.label.text}
      </span>
      <span style={{ flexShrink: 0, color: 'var(--text-mute)', opacity: 0.6 }}>|</span>
      <span
        key={tickerKey}
        style={{
          color: 'var(--text-soft)',
          fontStyle: 'italic',
          fontFamily: currentTool
            ? 'ui-monospace, SFMono-Regular, monospace'
            : 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 260,
          animation: 'sd-ticker-in 180ms ease-out',
        }}
      >
        {tickerText}
      </span>
    </div>
  );
}

/** Strip verbose tool prefixes so "mcp__shiftagent_platform__create_plan" → "create_plan". */
function shortenToolName(name: string): string {
  const parts = name.split('__').filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1]! : name;
}
